import React, { useState, useEffect, useContext } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, Grid, RadioGroup, FormControlLabel, Radio,
  Autocomplete, Typography, InputAdornment, Chip, Box,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { CloseDialogAction } from '../../../../components/shared/dialogs/CloseDialogAction';
import { isDemandModifierFormValid } from './DemandModifierValidation';
import {
  DemandModifier,
  DemandModifierFormData,
  DemandModifierTargetType,
  DemandModifierLocationType,
  DemandModifierType,
  DemandModifierTargetTypeValue,
  DemandModifierLocationTypeValue,
  DemandModifierTypeValue,
} from '../../DemandModifiers/types/DemandModifierTypes';
import { UserContext } from '../../../../components/shared/useUser';
import { client, Market } from '../../../../app/services/api/orderManagementClient';
import useLogError from '../../../../hooks/useLogError';

interface DemandModifierModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (modifier: DemandModifier) => Promise<void>;
  modifier?: DemandModifier | null;
  categories?: { id: number; name: string }[];
  isEditMode?: boolean;
  skuError?: string;
  onClearSkuError?: () => void;
}

const DemandModifierModal: React.FC<DemandModifierModalProps> = ({
  open,
  onClose,
  onSave,
  modifier,
  categories = [],
  isEditMode = false,
  skuError,
  onClearSkuError,
}) => {
  const { t } = useTranslation('bulkOrderManagement');
  const { franchises, stores, selectedStore } = useContext(UserContext);
  const { logError } = useLogError();

  const [availableFranchises, setAvailableFranchises] = useState<typeof franchises>([]);
  const [availableStores, setAvailableStores] = useState<typeof stores>([]);
  const [localCategories, setLocalCategories] = useState<{ id: number; name: string }[]>([]);
  const [skuInput, setSkuInput] = useState('');
  const [locationsLoading, setLocationsLoading] = useState(false);

  
  const resolvedCategories = categories && categories.length > 0 ? categories : localCategories;

  const [formData, setFormData] = useState<DemandModifierFormData>({
    name: '',
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: dayjs().add(30, 'days').format('YYYY-MM-DD'),
    targetType: DemandModifierTargetType.AllItems,
    selectedCategories: [],
    selectedSKUs: [],
    locationType: DemandModifierLocationType.SpecificFranchises,
    selectedFranchises: [],
    selectedStores: [],
    modifierType: DemandModifierType.PercentageUplift,
    upliftPercentage: 0,
    quantityOffset: 0,
  });

  useEffect(() => {
    let isCancelled = false;

    const loadMarketsForStore = async () => {
      setLocationsLoading(true);
      setAvailableFranchises([]);
      setAvailableStores([]);

      if (!selectedStore?.storeNumber) {
        setLocationsLoading(false);
        return;
      }

      try {
        const companyResult = await client.getCompanyIdByStoreNumber(selectedStore.storeNumber);
        if (isCancelled || !companyResult.success || !companyResult.data) {
          return;
        }

        const marketsResult = await client.getMarkets({
          companyId: companyResult.data.toString(),
          pageNumber: 0,
          pageSize: 2147483647,
        });

        if (isCancelled || !marketsResult.success || !marketsResult.data) {
          return;
        }

        const marketsData = marketsResult.data.items || [];
        const marketIds = new Set<number>(marketsData.map((market: Market) => market.customerTypeId));
        const mappedFranchises = marketsData.map((market: Market) => ({
          franchiseId: market.customerTypeId,
          franchiseName: market.name,
          active: true,
          systems: [],
        }));
        const filteredStores = stores.filter((store) => marketIds.has(store.franchiseId ?? -1));

        setAvailableFranchises(mappedFranchises);
        setAvailableStores(filteredStores.length > 0 ? filteredStores : stores);

       
        if (!categories || categories.length === 0) {
          try {
            const catResult = await client.getMarketCategories({
              companyId: companyResult.data.toString(),
              pageNumber: 0,
              pageSize: 2147483647,
            });
            if (!isCancelled && catResult.success && catResult.data?.items?.length > 0) {
              setLocalCategories(
                catResult.data.items.map((c) => ({ id: c.categoryId, name: c.name })),
              );
            }
          } catch (error) {
            logError(error);
          }
        }

      } catch (error) {
        logError(error);
        setAvailableFranchises([]);
        setAvailableStores([]);
      } finally {
        if (!isCancelled) {
          setLocationsLoading(false);
        }
      }
    };

    if (open) {
      if (!categories || categories.length === 0) {
        setLocalCategories([]);
      }
      loadMarketsForStore();
    }

    return () => {
      isCancelled = true;
    };
  }, [open, selectedStore?.storeNumber]);

  useEffect(() => {
    if (!open) return;

    if (modifier) {
      const storesForSelection = availableStores.length > 0 ? availableStores : stores;
      setFormData({
        name: modifier.name,
        startDate: modifier.startDate,
        endDate: modifier.endDate,
        targetType: modifier.targetType,
        selectedCategories: resolvedCategories.filter((cat) => modifier.targetCategories.includes(cat.id)),
        selectedSKUs: modifier.targetSKUs,
        locationType: modifier.locationType,
        selectedFranchises: availableFranchises.filter((f) => modifier.targetFranchises.includes(f.franchiseId ?? -1)),
        selectedStores: storesForSelection.filter((s) => modifier.targetStores.includes(s.storeNumber ?? '')),
        modifierType: modifier.modifierType,
        upliftPercentage: modifier.upliftPercentage,
        quantityOffset: modifier.quantityOffset ?? 0,
      });
    } else {
      resetForm();
    }
  }, [open, modifier, resolvedCategories, availableFranchises, availableStores, stores]);

  useEffect(() => {
    if (!open || !modifier || modifier.targetType !== DemandModifierTargetType.SpecificCategories) {
      return;
    }

    const selectedCategories = resolvedCategories.filter((cat) => modifier.targetCategories.includes(cat.id));

    setFormData((prev) => {
      const hasSameCategories = prev.selectedCategories.length === selectedCategories.length
        && prev.selectedCategories.every((category) => selectedCategories.some((selected) => selected.id === category.id));

      if (hasSameCategories) {
        return prev;
      }

      return {
        ...prev,
        selectedCategories,
      };
    });
  }, [open, modifier, resolvedCategories]);

  const resetForm = () => {
    setFormData({
      name: '',
      startDate: dayjs().format('YYYY-MM-DD'),
      endDate: dayjs().add(30, 'days').format('YYYY-MM-DD'),
      targetType: DemandModifierTargetType.AllItems,
      selectedCategories: [],
      selectedSKUs: [],
      locationType: DemandModifierLocationType.AllFranchises,
      selectedFranchises: [],
      selectedStores: [],
      modifierType: DemandModifierType.PercentageUplift,
      upliftPercentage: 0,
      quantityOffset: 0,
    });
  };

  const handleAddSku = () => {
    const sku = skuInput.trim().toUpperCase();
    if (!sku || formData.selectedSKUs.some((s) => s.toUpperCase() === sku)) return;
    setFormData({ ...formData, selectedSKUs: [...formData.selectedSKUs, sku] });
    setSkuInput('');
    onClearSkuError?.();
  };

  const handleRemoveSku = (sku: string) => {
    setFormData({ ...formData, selectedSKUs: formData.selectedSKUs.filter((s) => s !== sku) });
    onClearSkuError?.();
  };

  const handleSave = async () => {
    const demandModifier: DemandModifier = {
      id: modifier?.id,
      name: formData.name,
      startDate: formData.startDate,
      endDate: formData.endDate,
      targetType: formData.targetType,
      targetCategories: formData.selectedCategories.map(cat => cat.id),
      targetSKUs: formData.selectedSKUs,
      locationType: formData.locationType,
      targetFranchises: formData.selectedFranchises.map(f => f.franchiseId ?? -1),
      targetStores: formData.selectedStores.map(s => s.storeNumber ?? ''),
      modifierType: formData.modifierType,
      upliftPercentage: formData.upliftPercentage,
      quantityOffset: formData.modifierType === DemandModifierType.QuantityOffset ? formData.quantityOffset : undefined,
      isActive: modifier?.isActive ?? true,
    };

    await onSave(demandModifier);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {modifier ? t('demandModifier.editTitle') : t('demandModifier.createTitle')}
        <CloseDialogAction onCancel={onClose} />
      </DialogTitle>
      <DialogContent>
        <Grid container
          spacing={3}
          sx={{ mt: 1 }}>
          <Grid item
            xs={12}>
            <TextField
              fullWidth
              label={t('demandModifier.name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
            />
          </Grid>

          <Grid item
            xs={12}
            md={6}>
            <DatePicker
              label={t('demandModifier.startDate')}
              value={dayjs(formData.startDate)}
              onChange={(newValue) => setFormData({ ...formData, startDate: newValue?.format('YYYY-MM-DD') ?? '' })}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />
          </Grid>
          <Grid item
            xs={12}
            md={6}>
            <DatePicker
              label={t('demandModifier.endDate')}
              value={dayjs(formData.endDate)}
              onChange={(newValue) => setFormData({ ...formData, endDate: newValue?.format('YYYY-MM-DD') ?? '' })}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true,
                },
              }}
            />
          </Grid>

          <Grid item
            xs={12}>
            <Typography variant="textMD"
              fontWeight={600}
              sx={{ mb: 1 }}>
              {t('demandModifier.targetItems')}
            </Typography>
            <RadioGroup
              value={formData.targetType}
              onChange={(e) => setFormData({ ...formData, targetType: e.target.value as DemandModifierTargetTypeValue })}
            >
              <FormControlLabel
                value="allItems"
                control={<Radio />}
                label={t('demandModifier.allItems')}
              />
              <FormControlLabel
                value="specificCategories"
                control={<Radio />}
                label={t('demandModifier.specificCategories')}
              />
              <FormControlLabel
                value="specificSKUs"
                control={<Radio />}
                label={t('demandModifier.specificSKUs')}
              />
            </RadioGroup>

            {formData.targetType === DemandModifierTargetType.SpecificCategories && (
              <Autocomplete
                multiple
                options={resolvedCategories}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={formData.selectedCategories}
                onChange={(e, newValue) => setFormData({ ...formData, selectedCategories: newValue })}
                renderInput={(params) => (
                  <TextField {...params}
                    label={t('demandModifier.selectCategories')}
                    placeholder={t('demandModifier.chooseCategories')} />
                )}
                sx={{ mt: 2 }}
              />
            )}

            {formData.targetType === DemandModifierTargetType.SpecificSKUs && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label={t('demandModifier.enterSKU')}
                    value={skuInput}
                    onChange={(e) => { setSkuInput(e.target.value); onClearSkuError?.(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSku(); } }}
                    size="small"
                    error={!!skuError}
                    helperText={skuError}
                    sx={{ flexGrow: 1 }}
                  />
                  <Button variant="secondary"
                    onClick={handleAddSku}
                    size="lg">
                    {t('demandModifier.add')}
                  </Button>
                </Box>
                {formData.selectedSKUs.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {formData.selectedSKUs.map((sku) => (
                      <Chip
                        key={sku}
                        label={sku}
                        onDelete={() => handleRemoveSku(sku)}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Grid>

          {/* Target Locations Section */}
          <Grid item
            xs={12}>
            <Typography variant="textMD"
              fontWeight={600}
              sx={{ mb: 1 }}>
              {t('demandModifier.targetLocations')}
            </Typography>
            <RadioGroup
              value={formData.locationType}
              onChange={(e) => setFormData({ ...formData, locationType: e.target.value as DemandModifierLocationTypeValue })}
            >
              <FormControlLabel
                value="allFranchises"
                control={<Radio />}
                label={t('demandModifier.allFranchises')}
              />
              <FormControlLabel
                value="specificFranchises"
                control={<Radio />}
                label={t('demandModifier.specificFranchises')}
              />
              <FormControlLabel
                value="specificStores"
                control={<Radio />}
                label={t('demandModifier.specificStores')}
              />
            </RadioGroup>

            {formData.locationType === DemandModifierLocationType.SpecificFranchises && (
              <Autocomplete
                multiple
                options={availableFranchises}
                loading={locationsLoading}
                disabled={locationsLoading}
                getOptionLabel={(option) => option.franchiseName ?? ''}
                isOptionEqualToValue={(option, value) => option.franchiseId === value.franchiseId}
                value={formData.selectedFranchises}
                onChange={(e, newValue) => setFormData({ ...formData, selectedFranchises: newValue })}
                renderInput={(params) => (
                  <TextField {...params}
                    label={t('demandModifier.selectFranchises')}
                    placeholder={t('demandModifier.chooseFranchises')} />
                )}
                sx={{ mt: 2 }}
              />
            )}

            {formData.locationType === DemandModifierLocationType.SpecificStores && (
              <Autocomplete
                multiple
                options={availableStores}
                loading={locationsLoading}
                disabled={locationsLoading}
                getOptionLabel={(option) => option.storeName ?? option.storeNumber ?? ''}
                isOptionEqualToValue={(option, value) => option.storeNumber === value.storeNumber}
                value={formData.selectedStores}
                onChange={(e, newValue) => setFormData({ ...formData, selectedStores: newValue })}
                renderInput={(params) => (
                  <TextField {...params}
                    label={t('demandModifier.selectStores')}
                    placeholder={t('demandModifier.chooseStores')} />
                )}
                sx={{ mt: 2 }}
              />
            )}
          </Grid>

          {/* Modifier Type and Value */}
          <Grid item
            xs={12}>
            <Typography variant="textMD"
              fontWeight={600}
              sx={{ mb: 1 }}>
              {t('demandModifier.modifierType')}
            </Typography>
            <RadioGroup
              value={formData.modifierType}
              onChange={(e) => setFormData({ ...formData, modifierType: e.target.value as DemandModifierTypeValue })}
            >
              <FormControlLabel
                value="percentageUplift"
                control={<Radio />}
                label={t('demandModifier.percentageUplift')}
              />
              <FormControlLabel
                value="quantityOffset"
                control={<Radio />}
                label={t('demandModifier.quantityOffset')}
              />
            </RadioGroup>
          </Grid>

          {formData.modifierType === DemandModifierType.PercentageUplift && (
            <Grid item
              xs={12}>
              <TextField
                fullWidth
                type="number"
                label={t('demandModifier.upliftPercentage')}
                value={formData.upliftPercentage === 0 ? '' : formData.upliftPercentage}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  setFormData({
                    ...formData,
                    upliftPercentage: rawValue === '' ? 0 : Number(rawValue),
                  });
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                inputProps={{ max: 1000 }}
              />
            </Grid>
          )}

          {formData.modifierType === DemandModifierType.QuantityOffset && (
            <Grid item
              xs={12}>
              <TextField
                fullWidth
                type="number"
                label={t('demandModifier.quantityOffset')}
                value={formData.quantityOffset === 0 ? '' : formData.quantityOffset}
                onChange={(e) => {
                  const rawValue = e.target.value;
                  setFormData({
                    ...formData,
                    quantityOffset: rawValue === '' ? 0 : Number(rawValue),
                  });
                }}
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 5, pb: 5, pt: 4 }}>
        <Button variant="secondary"
          onClick={onClose}
          size="lg">
          {t('demandModifier.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={!isDemandModifierFormValid(formData)}
          size="lg"
        >
          {t('demandModifier.saveChanges')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DemandModifierModal;
